import { useEffect, useMemo, useRef, useState } from 'react'

import type { FilterConfig, RangeValue } from './FilterDropdown'

type Params<TFilterType extends string, TValue> = {
  filterConfigMap: Partial<Record<TFilterType, FilterConfig<TValue>>>
  isOptionChecked?: (filterType: TFilterType, value: TValue) => boolean
  onOptionsApply?: (filterType: TFilterType, values: TValue[]) => void
  onOptionToggle?: (filterType: TFilterType, value: TValue) => void
  getRangeInitialValue?: (filterType: TFilterType) => RangeValue
  onRangeApply?: (filterType: TFilterType, range: RangeValue) => void
  getSearchInitialValue?: (filterType: TFilterType) => { selectedOption?: TValue; searchText: string }
  onSearchApply?: (filterType: TFilterType, selectedOption: TValue, searchText: string) => void
  currencySymbol: string
  controlledOpen?: boolean
  onControlledOpenChange?: (open: boolean) => void
  openWithSubMenu?: TFilterType | null
}

// Sentinel value to distinguish "user explicitly returned to main list" from
// "no navigation yet" (null), which falls back to openWithSubMenu from the parent.
type SubMenuChoice<TFilterType extends string> = TFilterType | 'main' | null

export function useFilterDropdown<TFilterType extends string, TValue>({
  filterConfigMap,
  isOptionChecked,
  onOptionsApply,
  onOptionToggle,
  getRangeInitialValue,
  onRangeApply,
  getSearchInitialValue,
  onSearchApply,
  currencySymbol,
  controlledOpen,
  onControlledOpenChange,
  openWithSubMenu,
}: Params<TFilterType, TValue>) {
  const [internalOpen, setInternalOpen] = useState(false)
  const isControlled = controlledOpen !== undefined && onControlledOpenChange !== undefined
  const open = isControlled ? (controlledOpen as boolean) : internalOpen
  const setOpen = isControlled ? (onControlledOpenChange as (v: boolean) => void) : setInternalOpen

  const [filterSearchText, setFilterSearchText] = useState('')
  const [subMenuSearchText, setSubMenuSearchText] = useState('')
  // Tracks explicit user navigation within the panel.
  // null   = no navigation yet → activeSubMenu falls back to openWithSubMenu from parent
  // 'main' = user explicitly returned to the main filter list
  // TFilterType = user navigated to that specific submenu
  const [subMenuChoice, setSubMenuChoice] = useState<SubMenuChoice<TFilterType>>(null)
  const [localRangeValues, setLocalRangeValues] = useState<Record<string, RangeValue>>({})
  const [rangeSelectedField, setRangeSelectedField] = useState<Record<string, keyof RangeValue>>({})
  const [localSearchFilterValues, setLocalSearchFilterValues] = useState<
    Record<string, { selectedOption?: TValue; searchText: string }>
  >({})
  const [rangeValidationError, setRangeValidationError] = useState<string | null>(null)
  const [pendingOptionSelections, setPendingOptionSelections] = useState<Record<string, Set<string>>>({})
  const closeResetTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const pendingApplyRef = useRef<(() => void) | null>(null)

  useEffect(() => {
    return () => {
      if (closeResetTimeoutRef.current != null) {
        clearTimeout(closeResetTimeoutRef.current)
        closeResetTimeoutRef.current = null
      }
    }
  }, [])

  // Derive activeSubMenu at render time — no useEffect needed for prop→state sync.
  // When subMenuChoice is null (initial state or after reset), fall back to openWithSubMenu
  // so programmatic opens (e.g. clicking a filter chip) show the correct submenu directly.
  //
  // Note: intentionally NOT gating on `open` here. When the popover closes, the Radix exit
  // animation keeps the content visible for ~200ms while `open` is already false. Collapsing
  // to null immediately would flash the main list during that animation. Instead, subMenuChoice
  // is reset by the deferred resetFilterPanelState (see handleOpenChange) after the animation.
  const activeSubMenu: TFilterType | null =
    subMenuChoice === 'main'
      ? null
      : subMenuChoice !== null
        ? subMenuChoice
        : openWithSubMenu ?? null

  const activeSubMenuConfig = activeSubMenu ? filterConfigMap[activeSubMenu] : null

  const filteredFilterConfig = useMemo(() => {
    const entries = Object.entries(filterConfigMap ?? {}) as Array<[TFilterType, FilterConfig<TValue> | undefined]>
    return entries
      .filter(([, filter]) => Boolean(filter?.label))
      .filter(([, filter]) => {
        if (!filterSearchText) return true
        return (filter!.label ?? '').toLowerCase().includes(filterSearchText.toLowerCase())
      }) as Array<[TFilterType, FilterConfig<TValue>]>
  }, [filterConfigMap, filterSearchText])

  const filteredSubMenuOptions = useMemo(() => {
    const options = activeSubMenuConfig?.options ?? []
    const filtered = subMenuSearchText
      ? options.filter(option => option.label.toLowerCase().includes(subMenuSearchText.toLowerCase()))
      : options

    // Sort: committed selections (isOptionChecked) float to the top on open.
    if (!activeSubMenu || !isOptionChecked) return filtered
    return [...filtered].sort((a, b) => {
      const aChecked = isOptionChecked(activeSubMenu, a.value) ? 0 : 1
      const bChecked = isOptionChecked(activeSubMenu, b.value) ? 0 : 1
      return aChecked - bChecked
    })
  }, [activeSubMenuConfig, subMenuSearchText, activeSubMenu, isOptionChecked])

  const activeRangeValue = useMemo((): RangeValue => {
    if (!activeSubMenu) return { minPrice: '', maxPrice: '' }
    const key = String(activeSubMenu)
    return localRangeValues[key] ?? getRangeInitialValue?.(activeSubMenu) ?? { minPrice: '', maxPrice: '' }
  }, [activeSubMenu, localRangeValues, getRangeInitialValue])

  const activeSearchFilterValue = useMemo(() => {
    if (!activeSubMenu) return { searchText: '' as string, selectedOption: undefined as TValue | undefined }
    const key = String(activeSubMenu)
    return (
      localSearchFilterValues[key] ??
      getSearchInitialValue?.(activeSubMenu) ?? { searchText: '' }
    )
  }, [activeSubMenu, localSearchFilterValues, getSearchInitialValue])

  const effectivePendingSelections = useMemo(() => {
    if (!activeSubMenu) return pendingOptionSelections
    const key = String(activeSubMenu)
    if (pendingOptionSelections[key]) return pendingOptionSelections
    const config = filterConfigMap[activeSubMenu]
    const options = config?.options ?? []
    if (options.length === 0) return pendingOptionSelections
    const selected = new Set<string>()
    options.forEach(opt => {
      if (isOptionChecked?.(activeSubMenu, opt.value)) selected.add(String(opt.value))
    })
    return { ...pendingOptionSelections, [key]: selected }
  }, [activeSubMenu, pendingOptionSelections, filterConfigMap, isOptionChecked])

  const resetFilterPanelState = () => {
    setSubMenuChoice(null)
    setFilterSearchText('')
    setSubMenuSearchText('')
    setLocalRangeValues({})
    setRangeSelectedField({})
    setLocalSearchFilterValues({})
    setRangeValidationError(null)
    setPendingOptionSelections({})
  }

  const handleOpenChange = (isOpen: boolean) => {
    if (closeResetTimeoutRef.current != null) {
      clearTimeout(closeResetTimeoutRef.current)
      closeResetTimeoutRef.current = null
    }
    setOpen(isOpen)
    if (isOpen) {
      resetFilterPanelState()
    } else {
      // Defer reset to after Radix exit animation (~200ms) to avoid flashing main list.
      closeResetTimeoutRef.current = setTimeout(() => {
        closeResetTimeoutRef.current = null
        resetFilterPanelState()
      }, 200)
    }
  }

  const handleCategoryClick = (filterType: TFilterType) => {
    setRangeValidationError(null)
    setSubMenuChoice(filterType)
    setSubMenuSearchText('')
  }

  const handleBackClick = () => {
    setRangeValidationError(null)
    setSubMenuChoice('main')
    setSubMenuSearchText('')
  }

  const togglePendingOption = (filterType: TFilterType, value: TValue) => {
    const key = String(filterType)
    const valueStr = String(value)
    setPendingOptionSelections(prev => {
      const existingSet = prev[key]
      const base: Set<string> = existingSet ?? (() => {
        const options = filterConfigMap[filterType]?.options ?? []
        const initial = new Set<string>()
        options.forEach(opt => {
          if (isOptionChecked?.(filterType, opt.value)) initial.add(String(opt.value))
        })
        return initial
      })()
      const set = new Set(base)
      if (set.has(valueStr)) set.delete(valueStr)
      else set.add(valueStr)
      return { ...prev, [key]: set }
    })
  }

  const getPendingSetForFilter = (filterType: TFilterType, prev: Record<string, Set<string>>) => {
    const key = String(filterType)
    const existingSet = prev[key]
    if (existingSet) return { key, base: new Set(existingSet) }
    const options = filterConfigMap[filterType]?.options ?? []
    const initial = new Set<string>()
    options.forEach(opt => {
      if (isOptionChecked?.(filterType, opt.value)) initial.add(String(opt.value))
    })
    return { key, base: initial }
  }

  const toggleSelectAllFilteredOptions = (filterType: TFilterType) => {
    const options = filteredSubMenuOptions
    if (options.length === 0) return

    if (onOptionsApply) {
      setPendingOptionSelections(prev => {
        const { key, base } = getPendingSetForFilter(filterType, prev)
        const set = new Set(base)
        const filteredIds = options.map(o => String(o.value))
        const allSelected = filteredIds.every(id => set.has(id))
        if (allSelected) filteredIds.forEach(id => set.delete(id))
        else filteredIds.forEach(id => set.add(id))
        return { ...prev, [key]: set }
      })
      return
    }

    const allSelected = options.every(opt => isOptionChecked?.(filterType, opt.value))
    options.forEach(opt => {
      const checked = isOptionChecked?.(filterType, opt.value)
      if (allSelected && checked) onOptionToggle?.(filterType, opt.value)
      if (!allSelected && !checked) onOptionToggle?.(filterType, opt.value)
    })
  }

  const handleRangeFieldChange = (filterType: TFilterType, field: keyof RangeValue, value: string) => {
    setRangeValidationError(null)
    const key = String(filterType)
    setLocalRangeValues(prev => ({
      ...prev,
      [key]: {
        ...(prev[key] ?? getRangeInitialValue?.(filterType) ?? { minPrice: '', maxPrice: '' }),
        [field]: value,
      },
    }))
  }

  const handleRangeSelectFieldChange = (filterType: TFilterType, value: string) => {
    setRangeSelectedField(prev => ({
      ...prev,
      [String(filterType)]: value as keyof RangeValue,
    }))
  }

  const handleSearchOptionChange = (filterType: TFilterType, value: string) => {
    const key = String(filterType)
    setLocalSearchFilterValues(prev => ({
      ...prev,
      [key]: {
        ...(prev[key] ?? getSearchInitialValue?.(filterType) ?? { searchText: '' }),
        selectedOption: value as TValue,
      },
    }))
  }

  const handleSearchTextChange = (filterType: TFilterType, value: string) => {
    const key = String(filterType)
    setLocalSearchFilterValues(prev => ({
      ...prev,
      [key]: {
        ...(prev[key] ?? { selectedOption: undefined }),
        searchText: value,
      },
    }))
  }

  const validateRange = (
    range: RangeValue,
    config: FilterConfig<TValue> | null | undefined,
    symbol: string
  ): string | null => {
    if (!config?.isRangeFilter) return null
    const hasMin = range.minPrice.trim() !== ''
    const hasMax = range.maxPrice.trim() !== ''
    if (hasMin && !hasMax) return 'Please enter max value when min is set.'
    if (hasMax && !hasMin) return 'Please enter min value when max is set.'
    const { rangeMin, rangeMax } = config
    if (rangeMin == null && rangeMax == null) return null
    const minNum = range.minPrice.trim() === '' ? null : Number(range.minPrice)
    const maxNum = range.maxPrice.trim() === '' ? null : Number(range.maxPrice)
    if (minNum !== null && !Number.isFinite(minNum)) return 'Please enter a valid number for min.'
    if (maxNum !== null && !Number.isFinite(maxNum)) return 'Please enter a valid number for max.'
    if (minNum !== null && rangeMin != null && minNum < rangeMin)
      return `Min must be at least ${symbol}${rangeMin.toLocaleString()}.`
    if (minNum !== null && rangeMax != null && minNum > rangeMax)
      return `Min must be at most ${symbol}${rangeMax.toLocaleString()}.`
    if (maxNum !== null && rangeMin != null && maxNum < rangeMin)
      return `Max must be at least ${symbol}${rangeMin.toLocaleString()}.`
    if (maxNum !== null && rangeMax != null && maxNum > rangeMax)
      return `Max must be at most ${symbol}${rangeMax.toLocaleString()}.`
    if (minNum !== null && maxNum !== null && minNum > maxNum)
      return 'Min cannot be greater than max.'
    return null
  }

  const handleApply = () => {
    if (activeSubMenu && activeSubMenuConfig?.isRangeFilter) {
      const error = validateRange(activeRangeValue, activeSubMenuConfig, currencySymbol)
      if (error) {
        setRangeValidationError(error)
        return
      }
      setRangeValidationError(null)
    }

    // Snapshot current values before closing — hook state resets during the close animation.
    const subMenu = activeSubMenu
    const subMenuConfig = activeSubMenuConfig
    const rangeValue = activeRangeValue
    const searchValue = activeSearchFilterValue
    const pendingSelections = effectivePendingSelections

    // Store apply work to fire after the Radix close animation completes (via flushPendingApply).
    // This prevents Floating UI from repositioning the popover mid-close when new chips appear.
    pendingApplyRef.current = () => {
      if (subMenu && subMenuConfig?.isRangeFilter) {
        onRangeApply?.(subMenu, rangeValue)
      }
      if (subMenu && subMenuConfig?.isSearchFilter) {
        if (searchValue.selectedOption) {
          onSearchApply?.(subMenu, searchValue.selectedOption, searchValue.searchText)
        }
      }
      if (
        subMenu &&
        subMenuConfig?.options?.length &&
        !subMenuConfig?.isRangeFilter &&
        !subMenuConfig?.isSearchFilter &&
        onOptionsApply
      ) {
        const key = String(subMenu)
        const pending = pendingSelections[key]
        const options = subMenuConfig.options ?? []
        const selectedValues = options
          .filter(opt => pending?.has(String(opt.value)))
          .map(opt => opt.value)
        onOptionsApply(subMenu, selectedValues)
      }
    }

    handleOpenChange(false)
  }

  const flushPendingApply = () => {
    pendingApplyRef.current?.()
    pendingApplyRef.current = null
  }

  return {
    open,
    handleOpenChange,
    filterSearchText,
    setFilterSearchText,
    subMenuSearchText,
    setSubMenuSearchText,
    activeSubMenu,
    activeSubMenuConfig,
    filteredFilterConfig,
    filteredSubMenuOptions,
    activeRangeValue,
    activeSearchFilterValue,
    effectivePendingSelections,
    rangeSelectedField,
    rangeValidationError,
    handleCategoryClick,
    handleBackClick,
    togglePendingOption,
    toggleSelectAllFilteredOptions,
    handleRangeFieldChange,
    handleRangeSelectFieldChange,
    handleSearchOptionChange,
    flushPendingApply,
    handleSearchTextChange,
    handleApply,
  }
}

export type FilterDropdownHook<TFilterType extends string, TValue> = ReturnType<
  typeof useFilterDropdown<TFilterType, TValue>
>
